import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Flashbar,
    Header,
    Link,
    Modal,
    Popover,
    ProgressBar,
    Select,
    SpaceBetween,
    TextContent,
    Toggle
} from "@amzn/awsui-components-react";
import { AppContext } from "../../../setup/context";
import { useAPI2 } from "../../../hooks/api-hook";
import { EntitySelection } from "../../common/EntitySelection";
import {
    APIOutput,
    EntityRef,
    Instance,
    IPage,
    InstanceFactory,
    PageFactory,
    PageLibrary,
    REPORT_BUG_LINK
} from "@amzn/ask-legal-domain";
import { UIField } from "../../common/UIField";
import { ErrorFlashbar } from "../../common/ErrorFlashbar";
import { useNavigate } from "react-router-dom";
import { PageLibraryLink } from "../../pageLibrary/PageLibraryLink";
import { UIModel } from "../../../model/ui-model";
import { PageModel } from "../../../model/page-model";

export const DeepCopyPageModal = (props: {
    onDismiss: () => void;
    pageId: string;
    anticipatedExecutionTime?: number;
    prefetchedInstances?: { label: string; value: string }[];
}) => {
    const navigate = useNavigate();
    const context = React.useContext(AppContext);
    const currentInstanceId = InstanceFactory.fromEntityId(props.pageId);

    const [selectedInstance, setSelectedInstance] = React.useState<EntityRef>({ id: currentInstanceId } as EntityRef);
    const [instanceOptions, setInstanceOptions] = React.useState(
        props.prefetchedInstances || [{ label: currentInstanceId, value: currentInstanceId }]
    );
    const [instancesLoaded, setInstancesLoaded] = React.useState(!!props.prefetchedInstances);

    React.useEffect(() => {
        if (props.prefetchedInstances) return;
        context.getInstanceAPI().loadAll().then(res => {
            const output = APIOutput.fromRaw<Instance[]>(res.data);
            if (output.isOk()) {
                setInstanceOptions(output.data.map(inst => ({
                    label: inst.name || inst.id,
                    value: inst.id
                })).sort((a, b) => a.label.localeCompare(b.label)));
            }
            setInstancesLoaded(true);
        }).catch(() => setInstancesLoaded(true));
    }, []);
    const [targetPageLibrary, setTargetPageLibrary] = React.useState<EntityRef>(null);
    const [deprecateSourcePage, setDeprecateSourcePage] = React.useState<boolean>(false);
    const [progressBarValue, setProgressBarValue] = React.useState<number>(0);
    const [redirectionCountDown, setRedirectionCountDown] = React.useState<number>(5);
    const targetPageName = UIModel.State.useWithRegexAndCharLimit({
        initialValue: PageFactory.getNameFromPageId(props.pageId),
        regex: new RegExp(/^[a-zA-Z0-9]+$/),
        characterLimit: PageModel.TITLE_CHAR_LIMIT
    });

    const isCrossInstance = selectedInstance && selectedInstance.id !== currentInstanceId;

    const deepCopyPageRunner = useAPI2(
        context.getPageAPI().deepCopy
    );

    const triggerPageCopy = () => {
        const input: IPage.DeepCopyPageInput = IPage.DeepCopyPageInput.create({
            sourcePageId: props.pageId,
            targetPageId: PageFactory.toPageId(targetPageLibrary.id, targetPageName.value),
            deprecateSourcePage: deprecateSourcePage,
            ...(isCrossInstance && {
                targetInstanceId: selectedInstance.id,
                targetPageLibraryId: targetPageLibrary.id,
                newPageName: targetPageName.value,
            }),
        });
        deepCopyPageRunner.invoke(input);
    };

    // Reset page library when instance changes
    React.useEffect(() => {
        setTargetPageLibrary(null);
    }, [selectedInstance?.id]);

    React.useEffect(() => {
        let interval = null;
        let countdown = null;
        let delayedRedirection = null;
        if (deepCopyPageRunner.status === "Running") {
            interval = setInterval(() => {
                setProgressBarValue((prevValue) => {
                    if (prevValue < 90) return prevValue + 10;
                    else return 99;
                });
            }, 1000);
        } else if (deepCopyPageRunner.status === "Succeeded") {
            setProgressBarValue(100);
            countdown = setInterval(() => {
                if (redirectionCountDown > 0) {
                    setRedirectionCountDown((prevValue) => prevValue - 1);
                }
            }, 1000);

            delayedRedirection = setTimeout(() => {
                navigate(`/page/${deepCopyPageRunner.output.newPageRef.id}/live`);
            }, 5000);
        }
        return () => {
            clearTimeout(delayedRedirection);
            clearInterval(countdown);
            clearInterval(interval);
        };
    }, [deepCopyPageRunner.status]);

    const targetPageId = targetPageLibrary && targetPageName.value
        ? PageFactory.toPageId(targetPageLibrary.id, targetPageName.value)
        : null;

    return (
        <Modal
            visible={true}
            size="medium"
            header="Copy page"
            onDismiss={props.onDismiss}
            footer={
                <Box float="right">
                    <Button variant="link"
                        loading={deepCopyPageRunner.status === "Running"}
                        onClick={props.onDismiss}
                    >
                        Cancel
                    </Button>
                    <Button variant="primary"
                        loading={deepCopyPageRunner.status === "Running"}
                        disabled={
                            !selectedInstance ||
                            !targetPageLibrary ||
                            targetPageName.errorText ||
                            !targetPageName.value ||
                            (!isCrossInstance && props.pageId === PageFactory.toPageId(targetPageLibrary.id, targetPageName.value)) ||
                            deepCopyPageRunner.status === "Succeeded"
                        }
                        onClick={triggerPageCopy}
                    >
                        Confirm
                    </Button>
                </Box>
            }>
            {deepCopyPageRunner.status === "New" &&
                <SpaceBetween size="xs">
                    <UIField.CustomField
                        name="Select instance:"
                        child={<Select
                            selectedOption={instanceOptions.find(o => o.value === selectedInstance?.id) || null}
                            options={instanceOptions}
                            onChange={({ detail }) => setSelectedInstance({ id: detail.selectedOption.value } as EntityRef)}
                            disabled={deepCopyPageRunner.status !== "New"}
                            statusType={instancesLoaded ? "finished" : "loading"}
                            loadingText="Loading instances..."
                            filteringType="auto"
                            placeholder="Select instance"
                        />}
                    />
                    {selectedInstance &&
                        <UIField.CustomField
                            name="Please select a page library to copy the page to:"
                            child={<EntitySelection.SingleWithPagination<PageLibrary>
                                key={selectedInstance.id}
                                api={context.getPageLibraryAPI().loadAll}
                                partitionKey={selectedInstance.id}
                                pageSize={20}
                                selected={targetPageLibrary}
                                onSelectionChange={e => setTargetPageLibrary(e)}
                            />}
                        />
                    }
                    <UIField.StateValueField
                        name="New Page Name (part of the URL)"
                        state={targetPageName}
                        constraintText={
                            <React.Fragment>
                                Maximum {PageModel.TITLE_CHAR_LIMIT} characters <Popover
                                    dismissAriaLabel="Close"
                                    header="Page Naming Guidelines"
                                    triggerType="custom"
                                    content={<React.Fragment>
                                        <small>
                                            <ul>
                                                <li>Cannot contain spaces</li>
                                                <li>Cannot contain any special characters</li>
                                                <li>Can contain alphabets <b>A...Z</b> <b>a...z</b></li>
                                                <li>Can contain numbers <b>0-9</b></li>
                                                <li><b><em>Examples : </em></b> SamplePageName, 1New2Document3, ... etc</li>
                                            </ul>
                                        </small>
                                    </React.Fragment>}
                                >
                                    <Link variant="info">Format</Link>
                                </Popover></React.Fragment>}
                        editing={true}
                    />
                    <Toggle
                        checked={deprecateSourcePage}
                        onChange={(e: { detail: { checked: boolean; }; }) => setDeprecateSourcePage(e.detail.checked)}
                    >
                        Deprecate current page
                    </Toggle>
                </SpaceBetween>}
            {targetPageLibrary &&
                <Box padding={{ top: "m" }}>
                    <TextContent>Destination Page Library: <PageLibraryLink external id={targetPageLibrary.id} /><br /></TextContent>
                </Box>}
            {targetPageId && !targetPageName.errorText &&
                <Box padding={{ top: "m", bottom: "m" }}>
                    <TextContent>Destination Page URL: <small>/page/{targetPageId}/live</small><br /></TextContent>
                </Box>
            }
            {(deepCopyPageRunner.status === "Running" || deepCopyPageRunner.status === "Succeeded") && <ProgressBar
                value={progressBarValue}
                additionalInfo="Please do not refresh the page nor close the tab..."
                description="Please wait patiently while copying is in progress"
                label="Copying in progress"
            />}
            {deepCopyPageRunner.status === "Succeeded" && <Flashbar items={[{
                type: "success",
                content: <div>Copying was successful, you will be redirected in {redirectionCountDown} seconds.</div>,
                dismissible: false,
                id: "message_1"
            }]} />}
            {deepCopyPageRunner.status === "Error" &&
                <SpaceBetween size="m">
                    <ErrorFlashbar error={deepCopyPageRunner.err} />
                    {/* Temporary mechanism to inform user of timeout for larger pages */}
                    <Alert
                        statusIconAriaLabel="Warning"
                        type="warning"
                        header={<Header variant="h3">Note</Header>}
                    >
                        <TextContent>
                            If you encounter "<em>500: Endpoint request timed out</em>" error, please see the following message:
                            <br />
                            <ul>
                                <li>The page copy operation you initiated has encountered a timeout due to the size of the page. However, the operation may still be in progress.</li>
                                <li>Please be patient and check the <Link external variant="primary" href={`#/page/${targetPageId}/live`}>destination page URL</Link> after a few minutes.</li>

                            </ul>
                            <h4>Actions</h4>
                            <ol>
                                <li>If the page has been <strong>successfully</strong> copied, you will be able to access it.</li>
                                <li>If the page copy operation has <strong>not completed</strong> after a reasonable amount of time, please file a <Link external variant="primary" href={REPORT_BUG_LINK}>
                                    support ticket
                                </Link> with the technical team, and they will assist you further.</li>
                            </ol>
                        </TextContent>
                    </Alert>
                </SpaceBetween>
            }
        </Modal>
    );
};
